import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, MapPin, GraduationCap, User, Award, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AppContext";
import { GridBg } from "../../components/shared/GridBg";
import { Logo } from "../../components/shared/Logo";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { FileUpload } from "../../components/shared/FileUpload";
import { CITIES, SKILLS } from "../../data/mockData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { toast } from "sonner";

const STEPS = [
  { key: "personal",       label: "Personal",       icon: User },
  { key: "kyc",            label: "KYC",            icon: ShieldCheck },
  { key: "education",      label: "Education",      icon: GraduationCap },
  { key: "skills",         label: "Skills",         icon: Check },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "location",       label: "Location",       icon: MapPin },
];

export default function StudentOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [f, setF] = useState({
    name: user?.name || "", mobile: "", email: user?.email || "", city: "", location: "",
    kyc_file: null,
    college: "", university: "", course: "", degree: "", year: "", graduation_year: "",
    skills: [],
    certifications: [],
    service_city: "", service_radius: 10,
    mobile_verified: false,
  });

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggleSkill = (s) => setF(x => ({ ...x, skills: x.skills.includes(s) ? x.skills.filter(y => y !== s) : [...x.skills, s] }));
  const addCert = () => setF(x => ({ ...x, certifications: [...x.certifications, { title: "", issuer: "", year: "", file: null }] }));
  const updCert = (i, patch) => setF(x => ({ ...x, certifications: x.certifications.map((c, k) => k === i ? { ...c, ...patch } : c) }));
  const rmCert  = (i) => setF(x => ({ ...x, certifications: x.certifications.filter((_, k) => k !== i) }));
  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: f.name, mobile: f.mobile, email: f.email, city: f.city, location: f.location,
        kyc_file_id: f.kyc_file?.file_id || null,
        college: f.college, university: f.university, course: f.course, degree: f.degree,
        year: f.year, graduation_year: f.graduation_year,
        skills: f.skills,
        certifications: f.certifications.filter(c => c.title).map(c => ({
          title: c.title, issuer: c.issuer || null, year: c.year || null,
          file_id: c.file?.file_id || null,
        })),
        service_city: f.service_city, service_radius: f.service_radius,
        mobile_verified: f.mobile_verified,
      };
      await api.post("/profile/student", payload);
      await refresh();
      toast.success("Profile submitted", { description: f.kyc_file ? "KYC is under admin review — you'll be notified." : "Add KYC later to unlock applications." });
      navigate("/student", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSaving(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return;
    try {
      await api.post("/profile/verify-mobile", { mobile: f.mobile, otp });
      set("mobile_verified", true); setOtpOpen(false); setOtp("");
      toast.success("Mobile verified");
    } catch { toast.error("Invalid OTP"); }
  };

  const canNext = () => (step === 0 ? !!(f.name && f.email && f.city) : true);

  return (
    <GridBg className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <Logo/>
          <div className="text-xs text-zinc-500">Profile · <span className="text-blue-400 font-mono">{progress}% complete</span></div>
        </div>

        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const done = i < step, active = i === step;
            const Icon = s.icon;
            return (
              <React.Fragment key={s.key}>
                <div className="flex items-center gap-2 shrink-0" data-testid={`step-${s.key}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs"
                       style={{
                         background: done ? "#10B981" : active ? "rgba(59,130,246,0.15)" : "#111114",
                         border: `1px solid ${done ? "#10B981" : active ? "#3B82F6" : "#27272A"}`,
                         color: done ? "#0A0A0C" : active ? "#60A5FA" : "#71717A",
                       }}>
                    {done ? <Check size={12}/> : <Icon size={13}/>}
                  </div>
                  <span className={`text-xs uppercase tracking-widest ${active ? "text-white" : done ? "text-zinc-400" : "text-zinc-600"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: done ? "#10B981" : "#27272A" }}/>}
              </React.Fragment>
            );
          })}
        </div>

        <div className="card-surface p-8 max-w-3xl">
          {step === 0 && (
            <Panel title="Personal details" desc="Basic contact info we'll share with allocated companies.">
              <Row2>
                <Field label="Full name"><input data-testid="ob-name" value={f.name} onChange={e => set("name", e.target.value)} className={ic}/></Field>
                <Field label="Email">   <input data-testid="ob-email" value={f.email} onChange={e => set("email", e.target.value)} className={ic} disabled/></Field>
                <Field label="Mobile">
                  <div className="flex gap-2 items-stretch">
                    <input data-testid="ob-mobile" value={f.mobile} onChange={e => set("mobile", e.target.value)} placeholder="+91 98xxxxxxxx" className={ic}/>
                    {f.mobile_verified
                      ? <StatusBadge status="Verified"/>
                      : <button data-testid="ob-verify-mobile-btn" type="button" onClick={() => setOtpOpen(true)} className="btn-ghost px-3 rounded-md text-xs whitespace-nowrap" disabled={!f.mobile}>Verify OTP</button>}
                  </div>
                </Field>
                <Field label="City">
                  <select data-testid="ob-city" value={f.city} onChange={e => set("city", e.target.value)} className={ic + " appearance-none"}>
                    <option value="">Select…</option>{CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Location / neighbourhood" className="sm:col-span-2">
                  <input data-testid="ob-location" value={f.location} onChange={e => set("location", e.target.value)} placeholder="Andheri East" className={ic}/>
                </Field>
              </Row2>
            </Panel>
          )}

          {step === 1 && (
            <Panel title="KYC verification" desc="Upload Aadhaar or an equivalent photo ID. Admin verification typically takes under 4 hours.">
              <FileUpload
                value={f.kyc_file}
                onChange={v => set("kyc_file", v)}
                purpose="student_kyc"
                testId="ob-kyc-upload"
                hint="Upload Aadhaar / Identity document"
              />
              <div className="mt-2 flex items-center gap-3">
                <StatusBadge status={f.kyc_file ? "Pending" : "Rejected"}/>
                <span className="text-xs text-zinc-500">{f.kyc_file ? "Ready to submit for review" : "No document uploaded yet"}</span>
              </div>
              <div className="text-[11px] text-zinc-500">You must upload KYC to unlock task applications. You can also come back later from your profile.</div>
            </Panel>
          )}

          {step === 2 && (
            <Panel title="Education" desc="Where you studied helps us match you to relevant tasks.">
              <Row2>
                <Field label="College"><input data-testid="ob-college" value={f.college} onChange={e => set("college", e.target.value)} className={ic}/></Field>
                <Field label="University"><input data-testid="ob-university" value={f.university} onChange={e => set("university", e.target.value)} className={ic}/></Field>
                <Field label="Course"><input data-testid="ob-course" value={f.course} onChange={e => set("course", e.target.value)} placeholder="B.Com Hons" className={ic}/></Field>
                <Field label="Degree"><input data-testid="ob-degree" value={f.degree} onChange={e => set("degree", e.target.value)} placeholder="Bachelor" className={ic}/></Field>
                <Field label="Current year"><input data-testid="ob-year" value={f.year} onChange={e => set("year", e.target.value)} placeholder="3rd" className={ic}/></Field>
                <Field label="Graduation year"><input data-testid="ob-grad" value={f.graduation_year} onChange={e => set("graduation_year", e.target.value)} placeholder="2026" className={ic}/></Field>
              </Row2>
            </Panel>
          )}

          {step === 3 && (
            <Panel title="Skills" desc="Select all that apply — the more accurate, the better your match score.">
              <div className="flex flex-wrap gap-2">
                {SKILLS.map(s => {
                  const on = f.skills.includes(s);
                  return <button key={s} type="button" data-testid={`ob-skill-${s}`} onClick={() => toggleSkill(s)}
                                 className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${on ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
                    {s}
                  </button>;
                })}
              </div>
              <div className="mt-6 text-xs text-zinc-500">{f.skills.length} skill{f.skills.length !== 1 && "s"} selected</div>
            </Panel>
          )}

          {step === 4 && (
            <Panel title="Certifications" desc="Add relevant certifications. Optional — but boosts your match score.">
              <div className="space-y-4">
                {f.certifications.map((c, i) => (
                  <div key={i} className="rounded-md border p-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs text-zinc-500 font-mono">Certification #{i + 1}</div>
                      <button type="button" data-testid={`ob-cert-remove-${i}`} onClick={() => rmCert(i)} className="text-zinc-500 hover:text-rose-400 transition-colors duration-200">
                        <Trash2 size={13}/>
                      </button>
                    </div>
                    <Row2>
                      <Field label="Title"><input data-testid={`ob-cert-title-${i}`} value={c.title} onChange={e => updCert(i, { title: e.target.value })} placeholder="Advanced Excel — Wall Street Prep" className={ic}/></Field>
                      <Field label="Issuer"><input data-testid={`ob-cert-issuer-${i}`} value={c.issuer} onChange={e => updCert(i, { issuer: e.target.value })} placeholder="Wall Street Prep" className={ic}/></Field>
                      <Field label="Year"><input data-testid={`ob-cert-year-${i}`} value={c.year} onChange={e => updCert(i, { year: e.target.value })} placeholder="2025" className={ic}/></Field>
                    </Row2>
                    <div className="mt-3">
                      <FileUpload
                        value={c.file}
                        onChange={v => updCert(i, { file: v })}
                        purpose="certification"
                        testId={`ob-cert-file-${i}`}
                        label="Certificate file (optional)"
                        hint="Upload certificate PDF or image"
                      />
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="ob-cert-add" onClick={addCert} className="w-full py-3 rounded-md border border-dashed text-sm text-zinc-400 hover:text-white hover:border-blue-500/40 transition-colors duration-200"
                        style={{ borderColor: "var(--border-strong)" }}>
                  <Plus size={13} className="inline mr-1"/> Add certification
                </button>
              </div>
            </Panel>
          )}

          {step === 5 && (
            <Panel title="Service location" desc="Tasks within your service radius will be prioritised for you.">
              <Row2>
                <Field label="Service city">
                  <select data-testid="ob-service-city" value={f.service_city} onChange={e => set("service_city", e.target.value)} className={ic + " appearance-none"}>
                    <option value="">Select…</option>{CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label={`Service radius · ${f.service_radius} km`}>
                  <input data-testid="ob-radius" type="range" min={2} max={50} value={f.service_radius} onChange={e => set("service_radius", +e.target.value)} className="w-full accent-blue-500"/>
                </Field>
              </Row2>
              <div className="rounded-md overflow-hidden aspect-[16/6] relative border mt-2" style={{ borderColor: "var(--border)", background: "linear-gradient(135deg,#0f172a,#0b1220)" }}>
                <div className="absolute inset-0 grid-bg-fine opacity-70"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="rounded-full flex items-center justify-center pulse-dot"
                       style={{ width: `${Math.min(f.service_radius * 4, 180)}px`, height: `${Math.min(f.service_radius * 4, 180)}px`, background: "rgba(59,130,246,0.10)", border: "1px dashed rgba(59,130,246,0.5)" }}>
                    <MapPin size={14} className="text-blue-300"/>
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 glass rounded-md p-2 text-[11px] text-zinc-300 font-mono">
                  {f.service_city || "—"} · {f.service_radius} km radius
                </div>
              </div>
            </Panel>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
            <button data-testid="ob-back-btn" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
                    className="btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
              <ArrowLeft size={12}/> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button data-testid="ob-next-btn" onClick={() => canNext() && setStep(s => Math.min(STEPS.length - 1, s + 1))}
                      className="btn-primary px-5 py-2 rounded-full text-xs inline-flex items-center gap-1.5" disabled={!canNext()}>
                Continue <ArrowRight size={12}/>
              </button>
            ) : (
              <button data-testid="ob-submit-btn" onClick={submit} disabled={saving}
                      className="btn-primary px-5 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
                {saving ? "Saving…" : "Finish setup"} <Check size={12}/>
              </button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="max-w-sm" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Verify mobile</DialogTitle>
            <DialogDescription className="text-zinc-500">Enter the 6-digit code we sent to {f.mobile || "your mobile"}.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="ob-otp-input">
              <InputOTPGroup>
                {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="border-[#27272A] bg-[#111114] text-white"/>)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div className="text-[11px] text-zinc-500 text-center">Any 6 digits work in this preview</div>
          <DialogFooter>
            <button onClick={() => setOtpOpen(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button data-testid="ob-otp-verify-btn" onClick={verifyOtp} className="btn-primary px-4 py-2 rounded-md text-sm">Verify</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GridBg>
  );
}

const ic = "w-full px-3 py-2.5 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-blue-500/50 border-[#27272A] disabled:opacity-60";
const Panel = ({ title, desc, children }) => (
  <div>
    <h2 className="font-display text-2xl text-white">{title}</h2>
    <p className="text-sm text-zinc-500 mt-1.5">{desc}</p>
    <div className="mt-6 space-y-4">{children}</div>
  </div>
);
const Row2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
